// Upload a batch of takes, a few at a time. Exported so the scheduling can
// be exercised without a file dialog.
export async function uploadAudioBatch(files, { onStart, onProgress, onDone, onError } = {}) {
  // Several at once: one upload at a time leaves most of the uplink
  // idle while each request waits on the round trip, and a session is
  // usually a handful of takes dropped in together. Three keeps every
  // file's progress bar moving without starving any of them.
  const PARALLEL = 3;
  const results = new Array(files.length);
  let next = 0;

  const worker = async () => {
    while (next < files.length) {
      const index = next;
      next += 1;
      const file = files[index];
      if (onStart) try { onStart(file); } catch {}
      try {
        const arr = await uploadFilesWithProgress('/api/audio/upload', [file], {
          onProgress: (p) => { if (onProgress) try { onProgress(file, p); } catch {} },
        });
        const entry = Array.isArray(arr) ? arr[0] : arr;
        if (onDone) try { onDone(file, entry); } catch {}
        results[index] = entry || null;
      } catch (err) {
        if (onError) try { onError(file, err); } catch {}
        // Keep going — one bad file shouldn't kill the rest of the batch.
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(PARALLEL, files.length) }, worker));
  // Original order, minus whatever failed.
  return results.filter(Boolean);
}

// window.api shim. Mirrors the surface exposed by electron/preload.js so
// the React code in src/ runs unchanged. Every "path" returned by these
// methods is an opaque server-side id, which the renderer holds the same
// way it used to hold an OS path.

import {
  getJson, postJson, putJson, patchJson, delJson,
  uploadFiles, uploadFilesWithProgress,
  fileStreamUrl, filesZipUrl, authedFetch,
} from './http.js';
import { ensureConnected, onLog, onProgress } from './ws.js';
import { renderFinalMix, renderStems } from '@app/audio/exportRenderer.js';

// In-memory registry of client-rendered export blobs, keyed by the
// filename we hand back as the "output"/"files" id. project.export
// renders into this map; download()/downloadZip() read from it. A new
// export with the same filename simply overwrites the prior blob.
const exportBlobs = new Map(); // filename → Blob

// Whether this browser's audio decoder understands FLAC. Every current engine
// does (Chrome, Edge, Firefox, Safari 11+), but the check costs nothing and an
// old browser would otherwise be handed a file it cannot play. Probed once.
let _flacSupport = null;
function canDecodeFlac() {
  if (_flacSupport !== null) return _flacSupport;
  try {
    const probe = document.createElement('audio');
    const verdict = probe.canPlayType('audio/flac') || probe.canPlayType('audio/x-flac');
    _flacSupport = verdict === 'probably' || verdict === 'maybe';
  } catch {
    _flacSupport = false;
  }
  return _flacSupport;
}

function saveExportBlob(filename, blob) {
  exportBlobs.set(filename, blob);
  return filename;
}

function downloadBlob(filename, blob) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename || 'export.wav';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke after a delay so the download has time to start.
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

// File picker shim. Built-in dialogs don't exist on the web — instead we
// open a hidden <input type="file"> and resolve with the chosen Files.
function pickViaInput({ accept = '', multiple = false } = {}) {
  return new Promise((resolve) => {
    const input = document.createElement('input');
    input.type = 'file';
    if (accept) input.accept = accept;
    if (multiple) input.multiple = true;
    input.style.display = 'none';
    // Resolve to [] on cancel. There's no real cancel event in the spec;
    // we rely on focus returning to the window before any 'change' fires
    // — a 500ms timeout after focusin is the conventional workaround.
    let resolved = false;
    const done = (files) => { if (!resolved) { resolved = true; resolve(files); } };
    input.addEventListener('change', () => {
      done(Array.from(input.files || []));
      input.remove();
    });
    const onFocus = () => {
      setTimeout(() => {
        if (!resolved && (!input.files || input.files.length === 0)) {
          done([]);
          input.remove();
          window.removeEventListener('focus', onFocus);
        }
      }, 600);
    };
    window.addEventListener('focus', onFocus, { once: true });
    document.body.appendChild(input);
    input.click();
  });
}

// Browsers can't open Finder/Explorer, so "reveal an export" becomes
// "download it". A hidden <a download> click streams the file from
// /api/files/<id>?download=1, which the server sends with
// Content-Disposition: attachment.
function triggerDownload(url) {
  if (!url) return;
  const a = document.createElement('a');
  a.href = url;
  // Empty value defers the filename to the server's Content-Disposition.
  a.download = '';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  a.remove();
}

const AUDIO_ACCEPT = '.mp3,.wav,.m4a,.flac,.ogg,.aac,.opus,.mp4,audio/*';
const SCRIPT_ACCEPT = '.txt,.docx,.pdf';
const CSV_ACCEPT = '.csv,text/csv';
const PROJECT_ACCEPT = '.json,application/json';

export function installApi() {
  ensureConnected();

  const api = {
    audio: {
      // Optional callbacks for upload-progress UI:
      //   onStart(file)         — file selected, upload kicking off
      //   onProgress(file, p)   — p is 0..1 of THIS file's byte count
      //   onDone(file, entry)   — file finished successfully
      //   onError(file, error)  — file failed (others still proceed)
      // When any callback is provided we upload one File per POST so
      // the progress events are per-file. Without callbacks we keep the
      // batched POST (Electron-equivalent) for backwards compatibility.
      pickFiles: async ({ onStart, onProgress, onDone, onError } = {}) => {
        const files = await pickViaInput({ accept: AUDIO_ACCEPT, multiple: true });
        if (!files.length) return [];
        const wantCallbacks = !!(onStart || onProgress || onDone || onError);
        if (!wantCallbacks) {
          return uploadFiles('/api/audio/upload', files);
        }
        return uploadAudioBatch(files, { onStart, onProgress, onDone, onError });
      },

      probe: async (id) => postJson('/api/audio/probe', { id }),
      // The waveform outline for one file: tens of KB, computed and cached
      // by the server, so drawing never downloads the audio itself.
      peaks: async (id) => {
        const res = await authedFetch(`/api/audio/peaks/${encodeURIComponent(id)}`);
        if (!res.ok) throw new Error(`peaks: HTTP ${res.status}`);
        return res.arrayBuffer();
      },
      readBuffer: async (id) => {
        // Ask for the lossless FLAC copy when this browser can decode one.
        // Same samples, roughly half the bytes, and the bytes are what make
        // opening a project slow. The server falls back to the original
        // whenever it has no FLAC for a file, so this is safe to always send.
        const suffix = canDecodeFlac() ? '?fmt=flac' : '';
        const res = await authedFetch(`/api/audio/buffer/${encodeURIComponent(id)}${suffix}`);
        if (!res.ok) throw new Error(`readBuffer: HTTP ${res.status}`);
        return res.arrayBuffer();
      },
    },
    script: {
      pickFile: async () => {
        const files = await pickViaInput({ accept: SCRIPT_ACCEPT, multiple: false });
        if (!files.length) return null;
        // Server returns { id, name, lines } — hand it all back so the caller
        // can show the script's filename and skip a second parse round-trip.
        return uploadFiles('/api/script/upload', files, { fieldName: 'file', multi: false });
      },
      parse: async (id) => {
        const { lines } = await postJson('/api/script/parse', { id });
        return lines;
      },
    },
    transcribe: {
      file: (payload) => postJson('/api/transcribe/file', payload),
      health: (payload) => postJson('/api/transcribe/health', payload || {}),
      resetCsv: (payload) => postJson('/api/transcribe/resetCsv', payload || {}),
      csvPath: (payload) => postJson('/api/transcribe/csvPath', payload || {}),
      saveSegments: (payload) => postJson('/api/transcribe/saveSegments', payload || {}),
      openCsv: async (payload) => {
        // Two modes: caller passed a known id (rare; reuse cached upload)
        // or we open the file picker.
        if (payload && payload.csvPath) {
          return postJson('/api/transcribe/openCsv', { id: payload.csvPath });
        }
        const files = await pickViaInput({ accept: CSV_ACCEPT, multiple: false });
        if (!files.length) return null;
        return uploadFiles('/api/transcribe/openCsv', files, { fieldName: 'file', multi: false });
      },
    },
    stitch: {
      run: (payload) => postJson('/api/stitch/run', payload),
      // Auto-map script speakers → audio files by score. Returns
      // { speakers:[{name,dialogueCount}], mapping:{speaker: audioFileId} }.
      mapSpeakers: (payload) => postJson('/api/stitch/mapSpeakers', payload),
      pickEffects: async () => {
        const files = await pickViaInput({ accept: AUDIO_ACCEPT, multiple: true });
        if (!files.length) return [];
        return uploadFiles('/api/stitch/pickEffects', files);
      },
      pickOutputDir: () => postJson('/api/stitch/pickOutputDir', {}),
      reveal: (path) => postJson('/api/stitch/reveal', { path }),
    },
    project: {
      // Client-side export: render the mix in the browser with the Web
      // Audio API (see src/audio/exportRenderer.js) instead of POSTing to
      // the server's ffmpeg renderer. The heavy work runs on the user's
      // CPU; the server never renders, never queues, never freezes. The
      // returned shape matches the old server response so ActionButtons
      // (download/reveal) is unchanged — `output`/`files` are filenames
      // that download()/downloadZip() resolve from the in-memory blob map.
      export: async ({ project, mode } = {}) => {
        if (!project) throw new Error('export: no project');
        if (mode === 'stems') {
          const stems = await renderStems(project);
          const files = stems.map((s) => saveExportBlob(s.name, s.blob));
          return { mode: 'stems', outDir: 'your browser downloads', files };
        }
        const blob = await renderFinalMix(project);
        const name = 'final_mix.wav';
        saveExportBlob(name, blob);
        return { mode: 'final', output: name };
      },
      save: (payload) => postJson('/api/project/save', payload),
      load: (filePath) => postJson('/api/project/load', { filePath }),
      pickSavePath: (suggested) => postJson('/api/project/pickSavePath', { suggested }),
      pickLoadPath: async () => {
        const files = await pickViaInput({ accept: PROJECT_ACCEPT, multiple: false });
        if (!files.length) return null;
        return uploadFiles('/api/project/pickLoadPath', files, { fieldName: 'file', multi: false });
      },
      // Web-only: enumerate every project the server already has so the
      // renderer can offer "open by id" without re-uploading a JSON.
      // Returns [{id, name, createdAt, size, source}]. The Electron
      // preload doesn't expose this — the UI hides the menu when absent.
      list: () => getJson('/api/project/list'),
      // Web-only: change a saved project's display name (the editor title).
      rename: (id, name) => patchJson(`/api/project/${encodeURIComponent(id)}/name`, { name }),
      // Web-only: drop a server-stored project from the index + delete
      // its underlying JSON. Used by the trash button in the Open menu.
      delete: (id) => delJson(`/api/project/${encodeURIComponent(id)}`),
      // Electron reveals the file in the OS file manager; on web we
      // download it instead. `target` is an output-file id (the final
      // mix). Stems (multiple files) are downloaded via api.download from
      // the renderer, since reveal only carries a single target.
      reveal: (target) => {
        triggerDownload(fileStreamUrl(target, { download: true }));
        return Promise.resolve(true);
      },
    },
    settings: {
      get: () => getJson('/api/settings'),
      set: (patch) => putJson('/api/settings', patch || {}),
      // No directory picker on the web — return null so the renderer
      // either skips or shows a typeable input. The setting still
      // persists when the user edits it as plain text.
      pickDir: async () => null,
    },
    onLog,
    onProgress,
    // Used by src/utils/format.js#mediaUrl when present. Replaces the
    // Electron `media://localhost<path>` URL with an HTTP one.
    mediaUrl: (id) => fileStreamUrl(id),
    // Web-only: force a browser download of an output id. Client-rendered
    // exports live in the in-memory blob map (keyed by filename); anything
    // else is a server-stored file id streamed from /api/files/<id>.
    download: (id) => {
      const blob = exportBlobs.get(id);
      if (blob) { downloadBlob(id, blob); return; }
      triggerDownload(fileStreamUrl(id, { download: true }));
    },
    // Web-only: bundle several output ids into one .zip download (stems).
    // When every id is a client-rendered blob we zip in the browser with
    // JSZip; otherwise we fall back to the server's /api/files/zip route.
    downloadZip: async (ids, name) => {
      const list = (ids || []).filter(Boolean);
      if (list.length && list.every((id) => exportBlobs.has(id))) {
        const { default: JSZip } = await import('jszip');
        const zip = new JSZip();
        for (const id of list) zip.file(id, exportBlobs.get(id));
        const blob = await zip.generateAsync({ type: 'blob' });
        downloadBlob(name || 'stems.zip', blob);
        return;
      }
      triggerDownload(filesZipUrl(ids, name));
    },
  };

  window.api = api;
  return api;
}
